import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Receipt,
  Plus,
  PencilSimple,
  TrashSimple,
  CircleNotch,
  ArrowRight,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  fetchActivities,
  fetchActivityTotals,
  deleteActivity,
  convertToFeeRecord,
  type Activity,
  type ActivityType,
  type ActivityInvoiceStatus,
} from '@/lib/activitiesApi';
import { ActivityModal } from '@/components/activities/ActivityModal';
import { normaliseCategory } from '@jurivoxa/shared';
import { canLogActivity as canLogActivityFor } from '@/lib/activityAccess';
import { useAuthStore } from '@/features/auth/store';
import { getApiErrorMessage } from '@/lib/api';

interface Props {
  matterId: string;
}

const PAGE_LIMIT = 50;

function fmtMoney(value: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtDuration(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function statusBadgeClass(s: ActivityInvoiceStatus): string {
  switch (s) {
    case 'unbilled':     return 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30';
    case 'draft':        return 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30';
    case 'billed':       return 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30';
    case 'non_billable': return 'bg-zinc-100 text-zinc-700 dark:text-zinc-300 border-zinc-200';
  }
}

function statusLabel(s: ActivityInvoiceStatus): string {
  switch (s) {
    case 'unbilled':     return 'Unbilled';
    case 'draft':        return 'Draft';
    case 'billed':       return 'Billed';
    case 'non_billable': return 'Non-billable';
  }
}

export function MatterActivitiesPanel({ matterId }: Props) {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  // A pupil reads this ledger but cannot write to it — see ActivitiesPage for the full note.
  const canLogActivity = canLogActivityFor(currentUser?.memberType);

  const [createType, setCreateType] = useState<ActivityType | null>(null);
  const [editTarget, setEditTarget] = useState<Activity | null>(null);
  // Reading is not editing — see ActivitiesPage for the full note. Edit is refused on a billed
  // entry and on another person's, and it was the only way to open one, so the notes and rate
  // behind a billed item could be seen nowhere.
  const [viewTarget, setViewTarget] = useState<Activity | null>(null);
  const [page, setPage] = useState(1);

  const listQ = useQuery({
    queryKey: ['activities', { caseId: matterId, page }],
    queryFn: () => fetchActivities({ caseId: matterId, page, limit: PAGE_LIMIT }),
  });

  const totalsQ = useQuery({
    queryKey: ['activity-totals', { caseId: matterId }],
    queryFn: () => fetchActivityTotals({ caseId: matterId }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteActivity(id),
    onSuccess: () => {
      toast.success('Activity deleted');
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['activity-totals'] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary', matterId] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete activity')),
  });

  const convertMut = useMutation({
    mutationFn: (id: string) => convertToFeeRecord(id),
    onSuccess: () => {
      toast.success('Converted to fee record — visible in fee notes');
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['activity-totals'] });
      qc.invalidateQueries({ queryKey: ['fee-records'] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary', matterId] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not convert activity')),
  });

  return (
    <div className="space-y-4">
      {/* Top bar — totals + add buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {totalsQ.data ? (
            <>
              <strong className="text-foreground">{fmtDuration(totalsQ.data.totalMinutes)}</strong> logged ·
              {' '}<strong className="text-emerald-700 dark:text-emerald-300">{fmtMoney(totalsQ.data.billableAmount)}</strong> billable ·
              {' '}<strong>{fmtMoney(totalsQ.data.expenseAmount)}</strong> in expenses
            </>
          ) : totalsQ.isLoading ? (
            <span>Loading totals…</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {canLogActivity && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Receipt className="h-3.5 w-3.5" />}
              onClick={() => setCreateType('expense')}
            >
              Add expense
            </Button>
          )}
          {canLogActivity && (
          <Button
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setCreateType('time')}
          >
            Add time
          </Button>
          )}
        </div>
      </div>

      {/* List */}
      <Card className="overflow-hidden">
        {listQ.isLoading && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
            <CircleNotch size={14} className="animate-spin" /> Loading activities…
          </div>
        )}
        {listQ.isError && (
          <div className="px-5 py-6 text-sm text-red-700 dark:text-red-300">
            {getApiErrorMessage(listQ.error, 'Could not load activities.')}
          </div>
        )}
        {listQ.data && listQ.data.activities.length === 0 && (
          <div className="px-5 py-10 text-center">
            <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" weight="duotone" />
            <div className="mb-1 text-sm font-medium">No activities on this matter yet</div>
            <p className="mx-auto max-w-sm text-xs text-muted-foreground">
              Click <strong>Add time</strong> to log billable hours, or <strong>Add expense</strong> for disbursements
              like court fees and travel.
            </p>
          </div>
        )}
        {listQ.data && listQ.data.activities.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-[0.06em] text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Date</th>
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-left font-medium">User</th>
                <th className="px-4 py-2 text-right font-medium">Qty</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {listQ.data.activities.map((a) => {
                const canEdit = a.firmUserId === currentUser?.id && a.invoiceStatus !== 'billed';
                return (
                  <tr key={a.id} className="hover:bg-muted/20">
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                      {new Date(a.activityDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-2.5">
                      {a.entryType === 'time' ? (
                        <Badge variant="outline" className="gap-1">
                          <Clock size={11} weight="duotone" /> Time
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Receipt size={11} weight="duotone" /> Expense
                        </Badge>
                      )}
                    </td>
                    <td className="max-w-xs px-4 py-2.5">
                      <div className="truncate font-medium">{a.description}</div>
                      {a.category && (
                        // Normalised for display — see the same note on ActivitiesPage.
                        <div className="truncate text-xs text-muted-foreground">
                          {normaliseCategory(a.category)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[12.5px]">
                      {a.firmUser.firstName} {a.firmUser.lastName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-[12.5px]">
                      {a.entryType === 'time'
                        ? a.durationMinutes != null
                          ? fmtDuration(a.durationMinutes)
                          : '—'
                        : a.quantity ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      {fmtMoney(Number(a.amount), a.currency)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusBadgeClass(a.invoiceStatus)}`}
                      >
                        {statusLabel(a.invoiceStatus)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {a.isBillable && a.invoiceStatus === 'unbilled' && (
                          <button
                            onClick={() => {
                              if (window.confirm('Convert this activity to a fee record? This cannot be undone.')) {
                                convertMut.mutate(a.id);
                              }
                            }}
                            disabled={convertMut.isPending}
                            title="Convert to fee record"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-emerald-50 dark:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-300 dark:text-emerald-300 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <ArrowRight size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => setViewTarget(a)}
                          title="View"
                          aria-label="View activity"
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          onClick={() => setEditTarget(a)}
                          disabled={!canEdit}
                          title={canEdit ? 'Edit' : 'Cannot edit a billed entry, or one created by another user'}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <PencilSimple size={13} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this activity?')) {
                              deleteMut.mutate(a.id);
                            }
                          }}
                          disabled={!canEdit}
                          title={canEdit ? 'Delete' : 'Cannot delete a billed entry, or one created by another user'}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <TrashSimple size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {listQ.data && listQ.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-2.5 text-xs">
            <span className="text-muted-foreground">
              Page {listQ.data.pagination.page} of {listQ.data.pagination.totalPages} · {listQ.data.pagination.total} entries
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(listQ.data.pagination.totalPages, p + 1))}
                disabled={page >= listQ.data.pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {createType && (
        <ActivityModal
          matterId={matterId}
          defaultType={createType}
          onClose={() => setCreateType(null)}
          onSaved={() => setCreateType(null)}
        />
      )}
      {viewTarget && (
        <ActivityModal
          matterId={matterId}
          initial={viewTarget}
          readOnly
          onClose={() => setViewTarget(null)}
          onSaved={() => setViewTarget(null)}
        />
      )}
      {editTarget && (
        <ActivityModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
